import { useEffect } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';

import { SortingState } from '@cast/design-system';
import { QueryRefetchInterval } from '@cast/shared-types';
import {
  ListRunbookRuns,
  PRStatusEnum,
  RunbookRunStatusEnum,
} from '@cast/types-apa';
import { useCompoundReducer } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { hasSortingStateChanged } from 'utils/sort';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const APA_RUNBOOK_RUNS_QUERY_KEY = 'apa-runbook-runs';

type RunbookRunsFilters = {
  status?: RunbookRunStatusEnum;
  prStatus?: PRStatusEnum;
  searchTerm?: string;
};

export const getRunbookRunsQueryKey = ({
  runbookId,
  clusterId,
  pageLimit,
  currentPage,
  filters,
  sortingState,
}: {
  runbookId?: string;
  clusterId?: string;
  pageLimit?: number;
  currentPage?: number;
  filters?: RunbookRunsFilters;
  sortingState?: SortingState;
}) =>
  [
    APA_RUNBOOK_RUNS_QUERY_KEY,
    runbookId,
    clusterId,
    pageLimit,
    currentPage,
    filters,
    sortingState,
  ] as const;

type UseRunbookRunsQueryArgs = {
  runbookId?: string;
  clusterId?: string;
  filters?: RunbookRunsFilters;
  sortingState?: SortingState;
  pageLimit?: number;
  refetchInterval?: QueryRefetchInterval<ListRunbookRuns>;
  enabled?: boolean;
};

type PaginatedQueryState = {
  enabled: boolean;
  currentPage: number;
  currentCursor?: string;
  nextCursor?: string;
  visitedPages: string[];
  filters?: RunbookRunsFilters;
  sortingState?: SortingState;
};

const initialPaginationState = {
  currentPage: 1,
  currentCursor: '',
  visitedPages: [],
  nextCursor: undefined,
};

const initialState: PaginatedQueryState = {
  ...initialPaginationState,
  enabled: false,
  filters: {
    searchTerm: '',
  },
  sortingState: {
    columnId: 'updated_at',
    sortBy: {
      accessor: 'updated_at',
      direction: 'desc',
    },
  },
};

type RunbookRunsDispatcher = {
  dataLoaded: (data: ListRunbookRuns) => void;
};

const getRunbookRunsQueryOptions = ({
  runbookId,
  clusterId,
  pageLimit,
  refetchInterval,
  enabled,
  organizationId,
  state,
  dispatcher,
}: {
  runbookId?: string;
  clusterId?: string;
  pageLimit: number;
  refetchInterval: QueryRefetchInterval<ListRunbookRuns>;
  enabled: boolean;
  organizationId: string;
  state: PaginatedQueryState;
  dispatcher: RunbookRunsDispatcher;
}) =>
  queryOptions({
    queryKey: getRunbookRunsQueryKey({
      runbookId,
      clusterId,
      pageLimit,
      currentPage: state.currentPage,
      filters: state.filters,
      sortingState: state.sortingState,
    }),
    queryFn: async () => {
      const { data } = await apiClient.apa.getRunbookRuns({
        organizationId,
        clusterId,
        runbookId,
        pageCursor: state.currentCursor,
        pageLimit: String(pageLimit),
        status: state.filters?.status,
        prStatus: state.filters?.prStatus,
        searchTerm: state.filters?.searchTerm,
        sortField: state.sortingState?.columnId,
        sortOrder: state.sortingState?.sortBy.direction,
      });

      dispatcher.dataLoaded(data);
      return data;
    },
    refetchInterval,
    enabled: enabled && !!clusterId && !!runbookId,
  });

export const useRunbookRunsQuery = ({
  runbookId,
  clusterId,
  filters,
  sortingState,
  pageLimit = 50,
  refetchInterval = false,
  enabled = true,
}: UseRunbookRunsQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const { state, dispatcher } = useCompoundReducer(
    {
      setState: (
        state,
        action: {
          filters?: RunbookRunsFilters;
          sortingState?: SortingState;
          enabled: boolean;
        }
      ) => {
        if (
          action.enabled !== state.enabled ||
          !isEqual(action.filters, state.filters) ||
          hasSortingStateChanged(action.sortingState, state.sortingState)
        ) {
          return {
            ...initialState,
            enabled: action.enabled,
            filters: action.filters ?? state.filters,
            sortingState: action.sortingState ?? state.sortingState,
            ...initialPaginationState,
          };
        }
        return state;
      },
      dataLoaded: (state, data: ListRunbookRuns) => {
        return {
          ...state,
          nextCursor: data.nextPageCursor,
        };
      },
      fetchNextPage: (state) => {
        const currentCursor = state.currentCursor;
        const newPage = state.currentPage + 1;

        return {
          ...state,
          currentPage: newPage,
          visitedPages: state.visitedPages.concat(currentCursor!),
          currentCursor: state.nextCursor,
        };
      },
      fetchPreviousPage: (state) => {
        const newPage = state.currentPage - 1;
        const currentCursor = state.visitedPages[newPage - 1];

        return {
          ...state,
          currentPage: newPage,
          currentCursor,
          nextCursor: state.currentCursor,
        };
      },
    },
    initialState
  );

  const filterKey = JSON.stringify(filters);
  const sortingKey = JSON.stringify(sortingState);

  useEffect(() => {
    dispatcher.setState({ filters, sortingState, enabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, sortingKey, enabled]);

  const { data, isLoading, error, isFetching, refetch } = useQuery(
    getRunbookRunsQueryOptions({
      runbookId,
      clusterId,
      pageLimit,
      refetchInterval,
      enabled,
      organizationId: currentOrganization?.id ?? '',
      state,
      dispatcher,
    })
  );

  return {
    runbookRuns: data?.runbookRuns || [],
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage: () => {
      dispatcher.fetchNextPage();
    },
    fetchPreviousPage: () => {
      dispatcher.fetchPreviousPage();
    },
    hasNextPage: !!state.nextCursor,
    hasPreviousPage: !!state.visitedPages?.[state.currentPage - 1],
    metadata: {
      currentPage: state.currentPage,
      totalCount: data?.totalCount,
      pageSize: pageLimit,
    },
  };
};
